import careSvg from 'assets/icon/care.svg';
import cognitiveSvg from 'assets/icon/cognitive.svg';
import copySvg from 'assets/icon/copy.svg';
import maskSvg from 'assets/icon/mask.svg';
import pillCapsuleSvg from 'assets/icon/pill-capsule.svg';
import absentIcon from 'assets/icon/signout.svg';
import wormSvg from 'assets/icon/worm.svg';
import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { convertHex } from 'style/convertHex';
import styled from 'styled-components';

export enum VARIANT_STATE {
  COGNITIVE = 'cognitive',
  MULTIRESISTANT = 'multiresistant',
  INFECTIOUS = 'infectious',
  COFFIN = 'coffin',
  ABSENT = 'absent',
  COPY = 'copy',
  MEDICATION = 'medication',
}

type Props = {
  variant: string | any;
  messages?: string;
};

const ButtonStyled = styled.div<{ color: string | any; opacity: number | any; state: string }>`
  padding: 6px 8px 6px 4px;
  box-sizing: border-box;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  background-color: ${({ color, opacity, state }) =>
    state === VARIANT_STATE.COPY ? 'transparent' : convertHex(color, opacity)};
  color: ${({ color }) => color};
  min-width: 30px;

  display: flex;
  align-items: center;
  overflow: hidden;
  transition: 0.2s;
  & svg {
    max-width: 20px;
    min-width: 14px;
    min-height: 16px;
  }
  &:hover {
    background-color: ${({ color, opacity }) => convertHex(color, opacity)};
  }
`;

const BlockMessage = styled.span`
  line-height: 1.3em;
  white-space: nowrap;
  display: inline-block;
  padding-left: 4px;
  @media (max-width: 414px) {
    font-size: 8px;
  }
  &.is-hide {
    max-width: 0;
    overflow: hidden;
  }
  &.is-show {
    max-width: 575px;
    overflow: visible;

    > div {
      max-width: 575px;
      overflow: visible;
      @media (max-width: 1100px) {
        max-width: 190px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      @media (max-width: 768px) {
        max-width: 275px;
        white-space: unset;
        overflow: visible;
        text-overflow: ellipsis;
      }
    }
  }
  transition: max-width 0.5s;
  -webkit-transition: max-width 0.5s;
  -moz-transition: max-width 0.5s;
`;

const TooltipAction = ({ variant, messages }: Props) => {
  const { t } = useTranslation();
  const [dispalyContent, setDispalyContent] = useState(false);

  const displayIcon = (state: string) => {
    if (state === VARIANT_STATE.COGNITIVE) {
      return {
        color: '#783BF5',
        opacity: 0.1,
        icon: <SVG src={cognitiveSvg} />,
        messages: t('cognitiveLimitations'),
      };
    } else if (state === VARIANT_STATE.MULTIRESISTANT) {
      return {
        color: '#DE4087',
        opacity: 0.1,
        icon: <SVG src={wormSvg} />,
        messages: t('multi_resistantGerm'),
      };
    } else if (state === VARIANT_STATE.INFECTIOUS) {
      return {
        color: '#F5833B',
        opacity: 0.1,
        icon: <SVG src={maskSvg} />,
        messages: t('InfectiousDisease'),
      };
    } else if (state === VARIANT_STATE.COFFIN) {
      return {
        color: '#5E5A8D',
        opacity: 0.1,
        icon: <SVG src={careSvg} />,
        messages: t('DyingPhase'),
      };
    } else if (state === VARIANT_STATE.ABSENT) {
      return {
        color: '#FFA800',
        opacity: 0.1,
        icon: <SVG src={absentIcon} />,
        messages: t('absent'),
      };
    } else if (state === VARIANT_STATE.COPY) {
      return { color: '#5cabdd', opacity: 0.1, icon: <SVG src={copySvg} />, messages: messages };
    } else if (state === VARIANT_STATE.MEDICATION) {
      return {
        color: '#4CAF50',
        opacity: 0.1,
        icon: <SVG src={pillCapsuleSvg} />,
        messages: t('MedicationAdministration'),
      };
    } else {
      throw new Error(`Illegal State encountered: Case VARIANT_STATE ${state} not covered`);
    }
  };

  const handleMouseHover = (showContent: boolean) => {
    setDispalyContent(showContent);
  };

  const iconName = displayIcon(variant)?.icon;
  const color = displayIcon(variant)?.color;
  const opacity = displayIcon(variant)?.opacity;
  const messagesIcon = displayIcon(variant)?.messages;

  return (
    <ButtonStyled
      color={color}
      opacity={opacity}
      state={variant}
      {...(isMobile
        ? {
            onClick: () => handleMouseHover(true),
            onMouseLeave: () => handleMouseHover(false),
          }
        : {
            onMouseOver: () => handleMouseHover(true),
            onMouseLeave: () => handleMouseHover(false),
          })}
    >
      {
        <BlockMessage className={dispalyContent ? 'is-show' : 'is-hide'}>
          <div style={{ paddingRight: '3px' }}>{messagesIcon}</div>
        </BlockMessage>
      }
      {iconName}
    </ButtonStyled>
  );
};

export default TooltipAction;
